/**
 * The help assistant's conversation, as data.
 *
 * What to suggest to someone who opens the assistant and does not know what
 * to ask (by their job), how a reply's light markdown reads (bold, numbered
 * steps, bullets, «screen names»), and how much of the conversation is kept
 * and sent back.
 *
 * Pure: no React, no network.
 */

/** Questions people in each job actually ask, as one-tap chips. */
export const SUGGESTIONS = Object.freeze({
  receptionist: ['إزاي أطلع إيصال آجل؟', 'إزاي أوافق على طلب تحاليل؟', 'الدكتور أكد العملية في التليفون، أسجلها إزاي؟', 'إزاي أطبع تقرير الوردية؟'],
  operations_manager: ['إزاي أوزع السكرتارية على الدكاترة النهارده؟', 'دكتور اعتذر عن النهارده، أعمل إيه؟', 'إزاي أطبع جدول العيادات؟', 'إزاي أأجل مواعيد عيادة؟'],
  secretary: ['إزاي أحلل ملفات اليوم كلها؟', 'الفرق بين الأسرع والأذكى إيه؟', 'إزاي أدخل المريض اللي بعده؟', 'أنا مع أنهي دكتور النهارده؟'],
  doctor: ['إزاي أفتح الملف الطبي الموحد للمريض؟', 'التحليل الذكي بيقرا إيه بالظبط؟', 'إزاي أكتب روشتة وأطبعها؟', 'إزاي أوافق على عملية وأحدد موعدها؟'],
  contracts_manager: ['إزاي أوافق مالياً على طلب؟', 'إزاي أسجل تأكيد الجراح بالنيابة عنه؟', 'إزاي أضبط تغطية التأمين لمريض؟', 'إزاي أضيف جهة تعاقد؟'],
  accounts_manager: ['إزاي أسعّر طلب وأبعته للاستقبال؟', 'إزاي أطبع تقرير الوردية؟', 'إزاي ألغي إيصال؟'],
  lab_technician: ['إزاي أسجل نتيجة تحليل؟', 'الصور اللي برفعها النص بيتقري منها إزاي؟'],
  radiologist: ['إزاي أكتب تقرير الأشعة؟', 'إزاي أرفع صور الأشعة؟'],
  institution: ['إزاي أضيف موظف جديد؟', 'إزاي أغير شعار المستشفى؟', 'مين يقدر يوافق على الطلبات مالياً؟'],
  co_admin: ['إزاي أضيف موظف جديد؟', 'إزاي أعيد تعيين كلمة مرور موظف؟', 'مين يقدر يوافق على الطلبات مالياً؟'],
  admin: ['إزاي أفعّل النموذج المتقدم لدكتور؟', 'فين رسائل الدعم الفني؟', 'إزاي أدخل على مستشفى؟'],
  patient: ['إزاي أحجز مع دكتور؟', 'إزاي ألغي حجز؟', 'إزاي أضبط منبه الدواء؟'],
});

const COMMON = ['إزاي أغير الثيم؟', 'إزاي أغير كلمة المرور؟', 'عايز أكلم الدعم الفني'];

/** Up to six chips for this job, the common ones filling the gaps. */
export function suggestionsFor(role) {
  return [...(SUGGESTIONS[role] || []), ...COMMON].slice(0, 6);
}

/** How many turns are kept on screen and in storage. */
export const MAX_KEPT = 30;
/** How many go back to the server with a question (the server trims too). */
export const MAX_SENT = 12;

/** The turns the server needs: role and text, the newest MAX_SENT. */
export function historyForServer(messages) {
  return (Array.isArray(messages) ? messages : [])
    .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && String(m.content || '').trim() && m.status !== 'error')
    .slice(-MAX_SENT)
    .map((m) => ({ role: m.role, content: String(m.content) }));
}

/**
 * A reply as blocks: paragraphs, numbered steps and bullets, each a list of
 * inline segments (bold, «quoted UI names», plain).
 */
export function replyBlocks(text) {
  const blocks = [];
  for (const raw of String(text || '').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const step = /^(\d+)[.)-]\s+(.*)$/.exec(line);
    const bullet = /^[-*•]\s+(.*)$/.exec(line);
    const heading = /^#{1,4}\s+(.*)$/.exec(line);
    if (step) blocks.push({ type: 'step', n: Number(step[1]), segments: inlineSegments(step[2]) });
    else if (bullet) blocks.push({ type: 'bullet', segments: inlineSegments(bullet[1]) });
    else if (heading) blocks.push({ type: 'heading', segments: inlineSegments(heading[1]) });
    else blocks.push({ type: 'text', segments: inlineSegments(line) });
  }
  return blocks;
}

/** **bold**, «UI name», `code` — everything else plain. */
export function inlineSegments(text) {
  const out = [];
  const pattern = /\*\*(.+?)\*\*|«([^»]+)»|`([^`]+)`/g;
  let cursor = 0;
  let match;
  const value = String(text || '');
  while ((match = pattern.exec(value))) {
    if (match.index > cursor) out.push({ text: value.slice(cursor, match.index), kind: 'plain' });
    if (match[1] !== undefined) out.push({ text: match[1], kind: 'bold' });
    else if (match[2] !== undefined) out.push({ text: match[2], kind: 'ui' });
    else out.push({ text: match[3], kind: 'code' });
    cursor = match.index + match[0].length;
  }
  if (cursor < value.length) out.push({ text: value.slice(cursor), kind: 'plain' });
  return out.filter((s) => s.text !== '');
}

/** Where a user's conversation is kept in this browser. */
export const storageKey = (userId) => `capsule.help.chat.${userId || 'guest'}`;

let counter = 0;
export const messageId = () => `m${Date.now().toString(36)}${(counter += 1).toString(36)}`;
